<template>
  <div>
    <div class="mt-3 flex space-x-3 py-2">
      <UISwitch
        id="sms-notification-switch"
        v-model:value="isNotificationsEnabled"
        :aria-label="t('alt_text.sms_notification_switch')"
        :title="t('alt_text.sms_notification_switch')"
        class="w-1/3"
        size="small"
        @update:value="
          value => {
            toggleNotification(value)
          }
        "
      />
      <div class="items-center text-sm font-medium text-gray-700">
        {{
          isNotificationsEnabled
            ? t('eventNotification.enabled')
            : t('eventNotification.disabled')
        }}
      </div>
    </div>
    <div class="my-2 text-sm font-medium text-gray-500">
      {{ t('eventNotification.sms.notificationLabel') }}
    </div>

    <!-- Render all SMS templates of NotificationType - confirmation,reminder and others -->
    <UITooltip :trigger="!isNotificationsEnabled ? 'hover' : 'manual'">
      <template #trigger>
        <div :class="{ 'disabled-div': !isNotificationsEnabled }">
          <!-- Use template wrapper to allow v-if + v-for on separate elements (Vue 3 best practice).
               Guest notifications are kept in state.smsNotifications even when enableGuests is
               false so the toggle-all payload includes them; they are just hidden from the UI. -->
          <template
            v-for="(smsNotification, i) in state.smsNotifications"
            :key="'sms-notification-' + smsNotification.receiverType"
          >
            <div
              v-if="smsNotification.receiverType !== 'guest' || AppState.editCalendarValidationData.enableGuests"
            >
              <!-- Render a single SMS configurator window -->
              <SmsTemplateConfigurator
                ref="smsTemplateConfigurator"
                :index="i"
                :class="{
                  'rounded-t-lg border': i === 0,
                  'rounded-b-lg border': i === state.smsNotifications.length - 1,
                }"
                :notification="smsNotification"
                :is-notifications-enabled="isNotificationsEnabled"
              />
            </div>
          </template>
        </div>
      </template>
      <div>{{ t('eventNotification.tooltip') }}</div>
    </UITooltip>
  </div>
</template>

<script setup lang="ts">
import { EventNotificationService } from '@/class/services/EventNotificationService'
import { useNotify } from '@/components/composition/notification'
import AppState from '@/states/app'
import {
  getEventNotificationState,
  eventNotificationState as state,
  stripNotificationIdForCreate,
  updateNotificationState,
  isVirtualNotification,
} from '@/states/EventNotificationState'
import { UISwitch, UITooltip } from '@gohighlevel/ghl-ui'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SmsTemplateConfigurator from './SmsTemplateConfigurator.vue'
import {
  buildMeetingsSmsMergeFieldInputs,
  buildReceiverTemplateVariableSaveError,
  getSmsReceiverLabel,
  getSmsTemplateOptionsForValidation,
  passesTemplateVariableFieldValidation,
  type NotificationSaveValidationResult,
} from './notificationMergeFieldValidation'
const { t } = useI18n()
const { sendNotification } = useNotify()

const isNotificationsEnabled = ref(true)
const smsTemplateConfigurator = ref(null) as any
defineExpose({ validateAll })
const emit = defineEmits(['update-notification'])

isNotificationsEnabled.value = getEventNotificationState('sms')

/**
 *
 * @param value boolean
 * enable/disable all the SMS notification under this
 */
async function toggleNotification(value) {
  const payload = state.smsNotifications.map(notification => {
    return { ...notification, ...{ deleted: !value } }
  })
  state.disableSave = true
  const previousValue = isNotificationsEnabled.value
  try {
    isNotificationsEnabled.value = value

    const payloadToUpdate = payload.filter(
      notification => !isVirtualNotification(notification._id)
    )
    const payloadToCreate = payload
      .filter(notification => isVirtualNotification(notification._id))
      .map(stripNotificationIdForCreate)
    if (payloadToUpdate.length) {
      const { data } = await EventNotificationService.updateEventNotifications(
        payloadToUpdate
      )
      // Update local state with payload values (not API response)
      payloadToUpdate.forEach((item, index) => {
        const stateIndex = state.smsNotifications.findIndex(
          n => n._id === item._id || n.receiverType === item.receiverType
        )
        if (stateIndex !== -1) {
          state.smsNotifications[stateIndex] = {
            ...state.smsNotifications[stateIndex],
            deleted: !value,
          }
          // Also update the EventNotification instance state (used by Save function)
          if (state[item.receiverType]?.state) {
            state[item.receiverType].state.deleted = !value
          }
        }
      })
    }
    if (payloadToCreate.length) {
      const { data } = await EventNotificationService.createEventNotifications(
        payloadToCreate
      )
      updateNotificationState(data, !value)
    }
  } catch (error) {
    // Revert local state on error
    isNotificationsEnabled.value = previousValue
    sendNotification({
      type: 'error',
      title: 'Failed to update SMS notification',
      icon: 'AlertCircleIcon',
    })
    return
  } finally {
    state.disableSave = false
  }
  //update calendar notification if enabled
  handleOuterNotification(value, payload[0].notificationType)
  sendNotification({
    type: 'success',
    title: value ? 'SMS Notifications Enabled!' : 'SMS Notifications Disabled!',
    icon: 'CheckCircleIcon',
  })
}

function handleOuterNotification(value, type) {
  emit('update-notification', {
    channel: 'sms',
    type: type,
    status: value,
  })
}

function findConfiguratorByIndex(index: number) {
  const configurators = smsTemplateConfigurator.value ?? []
  return configurators.find(
    (component: { getIndex?: () => number }) => component?.getIndex?.() === index
  )
}

async function validateAll(): Promise<NotificationSaveValidationResult> {
  const results: boolean[] = []
  const templateOptions = getSmsTemplateOptionsForValidation()

  for (let index = 0; index < state.smsNotifications.length; index++) {
    const notification = state.smsNotifications[index]

    if (
      notification.receiverType === 'guest' &&
      !AppState.editCalendarValidationData.enableGuests
    ) {
      continue
    }

    const eventNotification = state[notification.receiverType]
    if (!eventNotification?.state?.isActive || eventNotification?.state?.deleted) {
      continue
    }

    const receiverLabel = getSmsReceiverLabel(notification.receiverType, t)
    const isOpen = !!state.accordions[index]?.isOpen
    const configurator = findConfiguratorByIndex(index)

    if (configurator?.validateForm) {
      try {
        await configurator.validateForm()
        results.push(true)
      } catch {
        results.push(false)
        const fields = buildMeetingsSmsMergeFieldInputs(
          eventNotification.state,
          templateOptions,
          t
        )
        if (!passesTemplateVariableFieldValidation(fields)) {
          await configurator?.revealTemplateVariableError?.(fields)
          return {
            results,
            errorMessage: buildReceiverTemplateVariableSaveError(
              receiverLabel,
              fields,
              t
            ),
            failedReceiverIndex: index,
          }
        }
        return {
          results,
          errorMessage: null,
          failedReceiverIndex: index,
        }
      }
    }

    if (!isOpen) {
      const fields = buildMeetingsSmsMergeFieldInputs(
        eventNotification.state,
        templateOptions,
        t
      )
      const isValid = passesTemplateVariableFieldValidation(fields)
      results.push(isValid)
      if (!isValid) {
        await configurator?.revealTemplateVariableError?.(fields)
        return {
          results,
          errorMessage: buildReceiverTemplateVariableSaveError(
            receiverLabel,
            fields,
            t
          ),
          failedReceiverIndex: index,
        }
      }
    }
  }

  return { results, errorMessage: null, failedReceiverIndex: null }
}
</script>

<style scoped>
.disabled-div {
  position: relative;
  opacity: 0.5;
}

.disabled-div input,
.disabled-div textarea,
.disabled-div button,
.disabled-div select {
  pointer-events: none;
  user-select: none;
}

.disabled-div .ui-checkbox,
.disabled-div .ui-switch {
  pointer-events: none;
}
</style>
